/**
 * Item Image Upload Service
 * Handles uploading images for individual items in a journey with proper structure
 */

import { uploadItemImages, uploadSingleItemImage } from '@/firebase/storageService';
import { auth } from '@/firebase/init';

/**
 * Upload images for a specific product/item in the journey
 * @param {File[]} files - Array of image files
 * @param {string} journeyId - Journey identifier
 * @param {string} itemName - Product/item name
 * @returns {Promise<string[]>} - Array of Firebase storage URLs
 */
export async function uploadImagesForItem(files, journeyId, itemName) {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    if (!Array.isArray(files) || files.length === 0) {
      return [];
    }

    // Upload all images with sequential naming
    const urls = await uploadItemImages(files, user.uid, journeyId, itemName);
    
    console.log(`Uploaded ${urls.length} images for item "${itemName}"`);
    return urls;
  } catch (error) {
    console.error('Error uploading images for item:', error);
    throw error;
  }
}

/**
 * Upload a single image for an item at a specific index
 * @param {File} file - Image file
 * @param {string} journeyId - Journey identifier
 * @param {string} itemName - Product/item name
 * @param {number} imageIndex - Image index (1-based)
 * @returns {Promise<string>} - Firebase storage URL
 */
export async function uploadSingleImageForItem(file, journeyId, itemName, imageIndex) {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    if (!file) {
      throw new Error('No file provided');
    }

    // Upload single image
    const url = await uploadSingleItemImage(file, user.uid, journeyId, itemName, imageIndex);
    
    console.log(`Uploaded image-${imageIndex} for item "${itemName}"`);
    return url;
  } catch (error) {
    console.error('Error uploading single image for item:', error);
    throw error;
  }
}

/**
 * Process and upload images from ImageSelector component
 * Handles both File objects and existing URLs
 * @param {Array} images - Array of images (can be File objects or URLs)
 * @param {string} journeyId - Journey identifier
 * @param {string} itemName - Product/item name
 * @returns {Promise<string[]>} - Array of Firebase storage URLs
 */
export async function processAndUploadItemImages(images, journeyId, itemName) {
  try {
    if (!Array.isArray(images) || images.length === 0) {
      return [];
    }

    const user = auth.currentUser;
    if (!user) {
      console.warn('User not authenticated, cannot upload images');
      return [];
    }

    const urls = [];
    const filesToUpload = [];

    console.log(`📤 Processing ${images.length} images for upload...`);

    // Separate existing URLs from new files
    for (const img of images) {
      if (typeof img === 'string') {
        // Already a Firebase URL (not blob)
        if (!img.startsWith('blob:')) {
          console.log('   ✅ Keeping existing Firebase URL');
          urls.push(img);
        } else {
          console.log('   ⚠️ Ignoring blob URL (will upload file instead)');
        }
      } else if (img instanceof File) {
        // New file to upload
        console.log('   📁 Found File object to upload:', img.name);
        filesToUpload.push(img);
      } else if (img && img.file instanceof File) {
        // Object with file property - UPLOAD THIS
        console.log('   📁 Found File in object.file to upload:', img.file.name);
        filesToUpload.push(img.file);
      } else if (img && img.url && typeof img.url === 'string') {
        // Object with URL property
        // Only keep if it's a Firebase URL, not a blob URL
        if (!img.url.startsWith('blob:')) {
          console.log('   ✅ Keeping Firebase URL from object.url');
          urls.push(img.url);
        } else if (img.file instanceof File) {
          // Has blob URL but also has file - upload the file
          console.log('   📁 Found File in object with blob URL, uploading file:', img.file.name);
          filesToUpload.push(img.file);
        } else {
          console.log('   ❌ Blob URL without file object - cannot upload');
        }
      }
    }

    // Upload new files if any
    if (filesToUpload.length > 0) {
      console.log(`   🚀 Uploading ${filesToUpload.length} files to Firebase Storage...`);
      const newUrls = await uploadItemImages(filesToUpload, user.uid, journeyId, itemName);
      console.log(`   ✅ Upload complete! Got ${newUrls.length} Firebase URLs`);
      urls.push(...newUrls);
    } else {
      console.log('   ⚠️ No files to upload!');
    }

    console.log(`   📦 Final result: ${urls.length} Firebase URLs`);
    return urls;
  } catch (error) {
    console.error('Error processing and uploading item images:', error);
    throw error;
  }
}

/**
 * Extract image URLs from various image formats
 * @param {Array} images - Array of images in various formats
 * @returns {string[]} - Array of URL strings
 */
export function extractImageUrls(images) {
  if (!Array.isArray(images)) {
    return [];
  }

  return images.map(img => {
    if (typeof img === 'string') {
      return img;
    } else if (img && img.url && typeof img.url === 'string') {
      return img.url;
    } else if (img && img.storageUrl && typeof img.storageUrl === 'string') {
      return img.storageUrl;
    }
    return null;
  }).filter(Boolean);
}

/**
 * Create personalization entry for pictures
 * @param {string[]} imageUrls - Array of image URLs
 * @returns {Object} - Personalization entry
 */
export function createPicturesPersonalization(imageUrls) {
  return {
    type: 'pictures',
    input: imageUrls,
    timestamp: Date.now()
  };
}

/**
 * Create personalization entry for text
 * @param {string} text - Text input
 * @returns {Object} - Personalization entry
 */
export function createTextPersonalization(text) {
  return {
    type: 'text',
    input: text,
    timestamp: Date.now()
  };
}

/**
 * Create personalization entry for color
 * @param {string} colorHash - Color hash/hex code
 * @returns {Object} - Personalization entry
 */
export function createColorPersonalization(colorHash) {
  return {
    type: 'color',
    input: colorHash,
    timestamp: Date.now()
  };
}

/**
 * Create personalization entry for selection
 * @param {*} selection - Selection data
 * @returns {Object} - Personalization entry
 */
export function createSelectionPersonalization(selection) {
  return {
    type: 'selection',
    input: selection,
    timestamp: Date.now()
  };
}
